import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';

import { getCatalogBrands } from '../../api/catalogs';
import { publishAgeBadge } from './catalogBadges';
import SearchInput from '../SearchInput/SearchInput';
import CatalogWorkspace from './CatalogWorkspace';
import CatalogPagination from './CatalogPagination';
import './Catalogs.scss';

const Catalogs = () => {
  const { brandKey, integrationKey, sectionKey } = useParams();
  const BrandIntegration_id = integrationIdFromKey(integrationKey);
  const runNumber = runNumberFromKey(sectionKey);
  const section = sectionFromKey(sectionKey);
  return brandKey ? (
    <CatalogWorkspace brandKey={brandKey} BrandIntegration_id={BrandIntegration_id} runNumber={runNumber} section={section} />
  ) : (
    <CatalogBrandDirectory />
  );
};

const integrationIdFromKey = integrationKey => {
  const match = String(integrationKey || '').match(/^integration-(\d+)$/);
  return match ? Number(match[1]) : null;
};

const runNumberFromKey = sectionKey => {
  const match = String(sectionKey || '').match(/^run-(\d+)$/);
  return match ? Number(match[1]) : null;
};

const sectionFromKey = sectionKey => (sectionKey === 'versions' ? 'versions' : 'catalog');

const CatalogBrandDirectory = () => {
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [systemFilter, setSystemFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [filterCounts, setFilterCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 250);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError(null);
    getCatalogBrands({ page, query, filter: statusFilter, system: systemFilter })
      .then(result => {
        if (!isActive) return;
        setBrands(result.brands);
        setTotal(result.total);
        setFilterCounts(result.counts);
        setPageSize(result.pageSize);
      })
      .catch(requestError => isActive && setError(requestError))
      .finally(() => isActive && setIsLoading(false));
    return () => {
      isActive = false;
    };
  }, [page, query, statusFilter, systemFilter]);

  const hasActiveRuns = brands.some(brand => brand.activeRunCount > 0);
  useEffect(() => {
    if (!hasActiveRuns) return undefined;
    let isActive = true;
    const refresh = () => {
      getCatalogBrands({ page, query, filter: statusFilter, system: systemFilter })
        .then(result => {
          if (!isActive) return;
          setBrands(result.brands);
          setTotal(result.total);
          setFilterCounts(result.counts);
        })
        .catch(() => {});
    };
    const interval = setInterval(refresh, 3000);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [hasActiveRuns, page, query, statusFilter, systemFilter]);

  const showFiltered = (system, status) => {
    setSystemFilter(system);
    setStatusFilter(status);
    setPage(0);
  };
  const draftsToReview = Number(filterCounts.status?.draft_ready || 0);
  const needsAttention = Number(filterCounts.status?.attention || 0);
  const heroStats = [
    { label: 'Brands', value: total, onClick: () => showFiltered('all', 'all') },
    { label: 'V2 catalogs', value: Number(filterCounts.system?.v2 || 0), onClick: () => showFiltered('v2', 'all') },
    { label: 'Drafts to review', value: draftsToReview, showAlert: draftsToReview > 0, onClick: () => showFiltered('all', 'draft_ready') },
    { label: 'Needs attention', value: needsAttention, showAlert: needsAttention > 0, onClick: () => showFiltered('all', 'attention') }
  ];

  return (
    <div className='catalogs-page'>
      <div className='catalogs-hero'>
        <h1>Catalogs</h1>
        <div className='catalogs-hero-stats'>
          {heroStats.map(stat => <HeroStat key={stat.label} {...stat} />)}
        </div>
      </div>

      <div className='catalogs-directory-tools'>
        <SearchInput
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder='Search by brand name, domain, or ID…'
          className='catalogs-directory-search'
        />

        <div className='catalogs-directory-filter-group'>
          <span className='catalogs-directory-filter-label'>System</span>
          <div className='catalogs-directory-filters'>
            {SYSTEM_FILTERS.map(option => (
              <FilterButton
                isActive={systemFilter === option.key}
                count={filterCounts.system?.[option.key]}
                key={option.key}
                label={option.label}
                onClick={() => {
                  setSystemFilter(option.key);
                  setPage(0);
                }}
              />
            ))}
          </div>
        </div>

        <div className='catalogs-directory-filter-group'>
          <span className='catalogs-directory-filter-label'>Status</span>
          <div className='catalogs-directory-filters'>
            {STATUS_FILTERS.map(option => (
              <FilterButton
                isActive={statusFilter === option.key}
                count={filterCounts.status?.[option.key]}
                key={option.key}
                label={option.label}
                onClick={() => {
                  setStatusFilter(option.key);
                  setPage(0);
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='catalogs-empty'>Loading brand catalogs…</div>
      ) : error ? (
        <div className='catalogs-error'>{error}</div>
      ) : (
        <>
          <div className='catalogs-brand-list'>
            {brands.map(brand => <BrandRow brand={brand} key={brand.Brand_id} />)}
            {!brands.length && <div className='catalogs-empty'>No brands match this search and filter.</div>}
          </div>
          {total > pageSize && <CatalogPagination onPage={setPage} page={page} pages={Math.ceil(total / pageSize)} />}
        </>
      )}
    </div>
  );
};

const SYSTEM_FILTERS = [
  { key: 'all', label: 'All catalogs' },
  { key: 'legacy', label: 'Legacy' },
  { key: 'v2', label: 'V2' }
];

const STATUS_FILTERS = [
  { key: 'all', label: 'All statuses' },
  { key: 'live', label: 'Live' },
  { key: 'draft_ready', label: 'Draft ready' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'not_started', label: 'Not started' }
];

const FilterButton = ({ count, isActive, label, onClick }) => (
  <button className={cn({ active: isActive })} onClick={onClick}>
    {label}
    <span className='catalogs-filter-count'>{Number(count || 0).toLocaleString()}</span>
  </button>
);

const BrandRow = ({ brand }) => {
  const hasIntegrations = Boolean(brand.integrations?.length);
  const integrations = hasIntegrations
    ? brand.integrations
    : [
        {
          id: `brand-${brand.Brand_id}`,
          domain: null,
          liveState: brand.liveState,
          pipelineState: brand.pipelineState,
          pipelineRun: brand.pipelineRun,
          activeRunCount: brand.activeRunCount,
          publishedAt: brand.publishedAt
        }
      ];
  const destination = hasIntegrations
    ? `/catalogs/${brand.brandKey}/integration-${integrations[0].id}`
    : `/catalogs/${brand.brandKey}`;
  return (
    <Link className='catalogs-brand-row' to={destination}>
      <div className='catalogs-brand-monogram'>{brandMonogram(brand.name)}</div>
      <div className='catalogs-brand-copy'>
        <div className='catalogs-brand-title'>{brand.name}</div>
        <div className='catalogs-muted'>{brand.domain || 'No domain'} · Brand ID {brand.Brand_id}</div>
      </div>
      <div className='catalogs-brand-statuses'>
        {integrations.map(integration => (
          <BrandIntegrationStatus integration={integration} hasIntegrations={hasIntegrations} key={integration.id} />
        ))}
      </div>
      <div className='catalogs-row-arrow'>→</div>
    </Link>
  );
};

const BrandIntegrationStatus = ({ hasIntegrations, integration }) => {
  const activity = describeIntegrationActivity(integration);
  const publication = describeIntegrationPublication(integration);
  const domain = integrationDomain(integration, hasIntegrations);
  return (
    <div className='catalogs-brand-integration'>
      <div className='catalogs-brand-integration-context'>
        {activity && (
          <>
            <span className={cn('catalogs-brand-integration-activity', activity.tone)} title={activity.title}>{activity.label}</span>
            <span aria-hidden='true' className='catalogs-brand-integration-separator'>·</span>
          </>
        )}
        <span className='catalogs-brand-integration-domain' title={domain}>{domain}</span>
      </div>
      <span className={cn('catalogs-status-badge', publication.tone)} title={publication.title}>{publication.label}</span>
    </div>
  );
};

const describeIntegrationPublication = integration => {
  const publishAge = publishAgeBadge(integration.publishedAt);
  if (publishAge) {
    return {
      label: `Published ${publishAge.ageLabel}`,
      tone: publishAge.tone,
      title: moment(integration.publishedAt).format('MMM D, YYYY h:mm A')
    };
  }
  if (['draft_ready', 'draft_running', 'publishing'].includes(integration.pipelineState)) return { label: 'Not published' };
  if (['failed', 'misconfigured'].includes(integration.liveState) || integration.pipelineState === 'run_failed') {
    return { label: 'Needs attention', tone: 'removed' };
  }
  return { label: 'Not started' };
};

const describeIntegrationActivity = integration => {
  if (['draft_running', 'publishing'].includes(integration.pipelineState)) {
    return { label: describePipelineRun(integration) || PIPELINE_STATES[integration.pipelineState].label, tone: 'in_progress' };
  }
  if (PIPELINE_STATES[integration.pipelineState]) return PIPELINE_STATES[integration.pipelineState];
  return ATTENTION_STATES[integration.liveState] || null;
};

const integrationDomain = (integration, hasIntegrations) => {
  if (!hasIntegrations) return 'No Shopify integration';
  return integration.domain || integration.shopName || `Integration ${integration.id}`;
};

const ATTENTION_STATES = {
  failed: { label: 'Needs repair', tone: 'removed' },
  misconfigured: {
    label: 'Misconfigured',
    tone: 'removed',
    title: 'The V2 flag is set but nothing is live, so this brand has no catalog syncing at all.'
  }
};

const PIPELINE_STATES = {
  draft_ready: { label: 'Ready to review', tone: 'needs_review' },
  draft_running: { label: 'Working…', tone: 'in_progress' },
  publishing: { label: 'Publishing…', tone: 'in_progress' },
  run_failed: { label: 'Version failed', tone: 'removed' }
};

const PIPELINE_STEP_LABELS = {
  snapshot: 'Pulling snapshot',
  build: 'Building catalog',
  checks: 'Running checks',
  publish: 'Publishing'
};

const describePipelineRun = integration => {
  const run = integration.pipelineRun;
  if (!run?.currentStep) return null;
  const requestedSteps = Object.values(run.steps || {}).filter(step => step.status !== 'not_requested');
  const step = run.steps?.[run.currentStep];
  const position = step?.position || requestedSteps.findIndex(candidate => candidate === step) + 1;
  const progress = position > 0 && requestedSteps.length ? `${position}/${requestedSteps.length}` : null;
  const count = integration.activeRunCount > 1 ? `${integration.activeRunCount} runs · ` : '';
  return `${count}${PIPELINE_STEP_LABELS[run.currentStep] || 'Processing'}${progress ? ` · ${progress}` : ''}…`;
};

const HeroStat = ({ label, onClick, showAlert, value }) => (
  <button className={cn('catalogs-hero-stat', { alert: showAlert })} onClick={onClick}>
    <strong>{Number(value || 0).toLocaleString()}</strong>
    <span>{label}</span>
  </button>
);

const brandMonogram = name =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

export default Catalogs;
